import { TimedMap } from '@lucidlink/tscore';
import { SettingName, SettingTarget, } from '#config/types';
import { FsLimits } from '#filesystem/types';
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const DEFAULT_SETTINGS_TIMEOUT = MINUTE;
const DEFAULT_SETTINGS_TIMEOUT_CHECK_INTERVAL = SECOND;
var SettingsKey;
(function (SettingsKey) {
    SettingsKey["FS_LIMITS"] = "fsLimits";
    SettingsKey["FORBID_SPECIAL"] = "forbidSpecial";
    SettingsKey["EXTERNAL_FILES_ENABLED"] = "externalFilesEnabled";
    SettingsKey["AUDIT_ENABLED"] = "auditEnabled";
    SettingsKey["FS_VERSION"] = "fsVersion";
})(SettingsKey || (SettingsKey = {}));
const SETTINGS_NAMES = new Map([
    [
        SettingsKey.FS_LIMITS,
        [
            {
                target: SettingTarget.FILE_SYSTEM,
                name: SettingName.FS_SIZE_LIMIT,
            },
            {
                target: SettingTarget.FILE_SYSTEM,
                name: SettingName.FS_ENTRIES_LIMIT,
            },
            {
                target: SettingTarget.FILE_SYSTEM,
                name: SettingName.FS_STORAGE_SIZE_LIMIT,
            },
            {
                target: SettingTarget.FILE_SYSTEM,
                name: SettingName.FS_EXTERNAL_FILES_SIZE_LIMIT,
            },
            {
                target: SettingTarget.FILE_SYSTEM,
                name: SettingName.FS_EXTERNAL_FILES_COUNT_LIMIT,
            },
        ],
    ],
    [
        SettingsKey.EXTERNAL_FILES_ENABLED,
        [
            {
                target: SettingTarget.FILE_SYSTEM,
                name: SettingName.EXTERNAL_FILES_ENABLED,
            },
        ],
    ],
    [
        SettingsKey.AUDIT_ENABLED,
        [
            {
                target: SettingTarget.AUDIT,
                name: SettingName.FEATURE_ENABLED,
            },
            {
                target: SettingTarget.AUDIT,
                name: SettingName.FS_EVENTS,
            },
        ],
    ],
    [
        SettingsKey.FORBID_SPECIAL,
        [
            {
                target: SettingTarget.FILE_SYSTEM,
                name: SettingName.FORBID_SPECIAL_CHARACTERS,
            },
        ],
    ],
    [
        SettingsKey.FS_VERSION,
        [
            {
                target: SettingTarget.FS_INIT,
                name: SettingName.FS_VERSION,
            },
        ],
    ],
]);
export class SettingsCache {
    config;
    settingsMap;
    constructor(config, timeoutMs = DEFAULT_SETTINGS_TIMEOUT, timeoutCheckIntervalMs = DEFAULT_SETTINGS_TIMEOUT_CHECK_INTERVAL) {
        this.config = config;
        this.settingsMap = new TimedMap({
            timeoutMs,
            timeoutCheckIntervalMs,
        });
    }
    clear() {
        this.settingsMap.clear();
    }
    stop() {
        this.clear();
        this.settingsMap.stopTimeoutCheck();
    }
    async getFsLimits() {
        const fsLimits = await this.getOrFetchSettings(SettingsKey.FS_LIMITS);
        return new FsLimits(fsLimits[SettingName.FS_SIZE_LIMIT].asUint64(), fsLimits[SettingName.FS_ENTRIES_LIMIT].asUint64(), fsLimits[SettingName.FS_STORAGE_SIZE_LIMIT].asUint64(), fsLimits[SettingName.FS_EXTERNAL_FILES_SIZE_LIMIT].asUint64(), fsLimits[SettingName.FS_EXTERNAL_FILES_COUNT_LIMIT].asUint64());
    }
    async getForbidSpecialCharacter() {
        const forbid = await this.getOrFetchSettings(SettingsKey.FORBID_SPECIAL);
        return forbid[SettingName.FORBID_SPECIAL_CHARACTERS].asBoolean();
    }
    async getExternalFilesEnabled() {
        const enabled = await this.getOrFetchSettings(SettingsKey.EXTERNAL_FILES_ENABLED);
        return enabled[SettingName.EXTERNAL_FILES_ENABLED].asOptionalBoolean();
    }
    async getAuditEnabled() {
        const settings = await this.getOrFetchSettings(SettingsKey.AUDIT_ENABLED);
        return settings[SettingName.FEATURE_ENABLED].asBoolean() && settings[SettingName.FS_EVENTS].asBoolean();
    }
    async getFsVersion() {
        const version = await this.getOrFetchSettings(SettingsKey.FS_VERSION);
        return version[SettingName.FS_VERSION].asUint32();
    }
    async getOrFetchSettings(key) {
        const settings = this.settingsMap.get(key);
        if (settings) {
            return settings;
        }
        return this.fetchSettings(key);
    }
    async fetchSettings(key) {
        const settingsList = await this.config.get(SETTINGS_NAMES.get(key));
        return this.parseAndCacheSettings(key, settingsList);
    }
    parseAndCacheSettings(key, settingsList) {
        const settings = {};
        for (const setting of settingsList) {
            settings[setting.fullName().name] = setting.effective();
        }
        this.settingsMap.set(key, settings);
        return settings;
    }
}
